import type { Child, Document } from '../api/authoring'
import { childByLinkId, isLinked, type PinState, pinStateOf } from './link'

// The Website type's own fields, read by hand.
//
// **This is debt with a name.** The manifest carries structure — types,
// their intents, containment, inlineability — and deliberately no field
// schemas: those are derived from the Go structs by the generator, which
// is a later phase. Until it runs, a client that wants to read a Website's
// routing tree has to know its shape, so it is declared here, keyed by
// type name, exactly as docs/goals/admin-client.md provides for.
//
// Two rules keep it from spreading. It describes **one type**, so a
// second type gets its own file rather than a shared "fields" module that
// would quietly become a second registry. And nothing structural is read
// from here — the palette, containment and the command vocabulary come
// from the manifest, because a hardcoded type list fails SPEC's
// acceptance checklist outright.
//
// Source of truth: internal/domain/component/types/website/website.go.

/**
 * The type a site is.
 *
 * Named once rather than in the four places that were spelling it out,
 * because three of them are a *fallback* for a document that has not
 * arrived yet and the fourth is the listing the navigation is built from.
 * It sits in this file for the same reason the field shapes do: it is a
 * fact about the Website type, and this is the file that admits to
 * knowing them.
 */
export const SITE_TYPE = 'Website'

export interface WebsiteSeo {
  description?: string
  keywords?: string
}

/**
 * One node of the routing tree: a slug, the page it serves, and its own
 * child routes.
 *
 * Routing lives on the Website rather than on the Page because a slug
 * belongs to the route that reaches a page, not to the page itself — the
 * same Page could be reachable at two routes, and moving a page in the
 * menu must not revise the page.
 */
export interface WebsiteRoute {
  slug?: string
  title?: string
  /** The **link id** of the placement serving this route, not a component id. */
  page?: string
  /**
   * The line a menu shows with the route's title. The route's rather than
   * the page's, like the title: it describes a link to the page. Not
   * search metadata, which is the page's own `seo`.
   */
  description?: string
  /**
   * The menus this route appears in.
   *
   * An open set: a site names its own, and `primary` is the one a Menu
   * draws when it names none. Absent or empty means the route is in no
   * menu — still reachable at its path, rendered by nothing that draws a
   * list of links.
   *
   * It replaced a `navVisible` boolean, which could describe exactly one
   * menu. Delivery still serves that boolean, derived from membership in
   * `primary`; authoring does not, because an editor writes back and a
   * derived field prefilled into a form is restated as a command the
   * author never made.
   */
  menus?: string[]
  /** Filters applied to generated-route menu tags only. */
  menuFilters?: WebsiteMenuFilter[]
  /** A generated node's identity, unique across this routing tree. */
  id?: string
  /** The binding name for each source record. */
  param?: string
  /** The Website reference edge naming the source Collection. */
  source?: string
  /** The Website reference edge naming the Page-producing pattern. */
  pattern?: string
  children?: WebsiteRoute[]
}

/** One generated-route menu tag narrowed to records with a matching field. */
export interface WebsiteMenuFilter {
  tag: string
  where: { field: string; equals: string | boolean }
}

/** Whether a node produces one page per member instead of serving one Page. */
export function generatedRoute(route: WebsiteRoute): boolean {
  return Boolean(route.id || route.source || route.pattern)
}

/** The menu a route joins when nothing else is said. */
export const PRIMARY_MENU = 'primary'

/** A route's menu set, however the document spelled it. */
export function routeMenus(route: WebsiteRoute | undefined): string[] {
  return route?.menus ?? []
}

/** Whether a route appears in a given menu. */
export function inMenu(route: WebsiteRoute | undefined, menu: string): boolean {
  return routeMenus(route).includes(menu)
}

/**
 * Every menu named anywhere in a routing tree, `primary` always
 * included.
 *
 * This is what makes the set open and still usable: the second route to
 * join the footer picks it from a list rather than retyping it, and a
 * typo shows up as a menu with one page in it instead of silently
 * joining nothing.
 */
export function menusInUse(routes: WebsiteRoute[] | undefined): string[] {
  const found = new Set<string>([PRIMARY_MENU])
  const walk = (nodes: WebsiteRoute[] | undefined) => {
    for (const node of nodes ?? []) {
      for (const menu of node.menus ?? []) found.add(menu)
      walk(node.children)
    }
  }
  walk(routes)
  return [...found].sort()
}

export interface WebsiteRouting {
  /** The link id of the placement answering "/". */
  home?: string
  nodes?: WebsiteRoute[]
}

export interface WebsiteDomain {
  host: string
  canonical?: boolean
}

export interface WebsiteFields {
  name?: string
  title?: string
  seo?: WebsiteSeo
  presentation?: string
  domains?: WebsiteDomain[]
  routing?: WebsiteRouting
  /** Slot name to the ordered link ids a page inherits when it declares no slot of its own. */
  defaults?: Record<string, string[]>
  /** The link id of the child composed for an unmatched path. */
  notFound?: string
}

export function websiteFields(document: Document): WebsiteFields {
  return (document.fields ?? {}) as WebsiteFields
}

/**
 * A route with everything needed to draw its row, resolved against the
 * document that holds it.
 *
 * The two halves are deliberately separate in the model and are joined
 * only here: `routing.nodes` is the Website's *field* vocabulary and
 * carries link ids; `document.children` is the envelope's placements and
 * carries the targets. A row needs both, and neither knows about the
 * other.
 */
export interface ResolvedRoute {
  route: WebsiteRoute
  /** The link path from the Website root to this route's placement. */
  path: string[]
  /** The URL path this route answers on, assembled from the slugs above it. */
  urlPath: string
  /** The placement serving it, absent when the route is a grouping node. */
  child?: Child
  /** The Page component this route reaches, when the placement is a linked one. */
  targetId?: string
  pin?: PinState
  /** Whether this route answers "/". */
  isHome: boolean
  depth: number
  children: ResolvedRoute[]
  /**
   * The page link id of the route this one sits under, empty at the top
   * level — which is exactly what `under` means on `website.page_added`
   * and `website.routes_reordered`.
   *
   * A **grouping route cannot be a parent**, and that is the model's
   * doing rather than an omission here: `routesUnder` finds a level by
   * the page its parent serves, so a route serving no page names no
   * level. Worth surfacing where an author would try it.
   */
  under: string
  /**
   * The page link ids at this route's own level, in order — what a
   * reorder sends.
   *
   * Grouping routes are absent, because a reorder is keyed by the page a
   * route serves and several empty keys would collide. The fold keeps
   * what an order omits, so they survive; they drift to the end of the
   * level, which is the honest cost of arranging a level that holds one.
   */
  siblings: string[]
  /**
   * Set when the route names a link id the document has no child for.
   * The model refuses to create one, so seeing it means something wrote
   * around the surface — worth showing rather than rendering a blank row.
   */
  dangling: boolean
  /** Whether this row produces a page for each record in a Collection. */
  generated: boolean
  /** The id a route order uses: a page placement or a generated-node id. */
  key: string
}

/**
 * Resolve the routing tree for display.
 *
 * A route with no page is a **grouping node** — it appears in navigation
 * without being reachable itself — so an absent `page` is ordinary and is
 * not the same as a dangling one.
 */
export function resolveRouting(document: Document): ResolvedRoute[] {
  const fields = websiteFields(document)
  const home = fields.routing?.home

  const walk = (
    routes: WebsiteRoute[],
    parentUrl: string,
    depth: number,
    under: string,
  ): ResolvedRoute[] => {
    const siblings = routes.flatMap((route) => route.page ? [route.page] : route.id ? [route.id] : [])
    return routes.map((route) => {
      const generated = generatedRoute(route)
      const key = route.page ?? route.id ?? ''
      const urlPath = generated
        ? joinPath(parentUrl, `:${route.param ?? 'record'}`)
        : joinPath(parentUrl, route.slug ?? '')
      const child = route.page ? childByLinkId(document.children, route.page) : undefined
      return {
        route,
        path: route.page ? [route.page] : [],
        urlPath,
        child,
        targetId: child && isLinked(child) ? child.id : undefined,
        pin: child ? pinStateOf(child) : undefined,
        isHome: Boolean(route.page && home === route.page),
        depth,
        children: walk(route.children ?? [], urlPath, depth + 1, key),
        dangling: Boolean(route.page) && child === undefined,
        generated,
        key,
        under,
        siblings,
      }
    })
  }

  return walk(fields.routing?.nodes ?? [], '', 0, '')
}

function joinPath(parent: string, slug: string): string {
  const cleaned = slug.replace(/^\/+|\/+$/g, '')
  if (!cleaned) return parent || '/'
  return `${parent === '/' ? '' : parent}/${cleaned}`
}

/** Every route in the tree, flattened depth-first — the order a list draws them in. */
export function flattenRoutes(routes: ResolvedRoute[]): ResolvedRoute[] {
  return routes.flatMap((route) => [route, ...flattenRoutes(route.children)])
}

/**
 * The site-level slot defaults: the header and footer every page inherits
 * unless it declares its own.
 *
 * The slot *names* here are read off the stored map, which is the only
 * place they are stated — nothing declares the vocabulary (B14). That is
 * survivable for an existing site and is exactly what breaks on a new
 * page, where there are no keys to read.
 */
export interface ResolvedSlot {
  slot: string
  placements: Array<
    { linkId: string; child?: Child; pin?: PinState; targetId?: string }
  >
}

export function resolveDefaults(document: Document): ResolvedSlot[] {
  const fields = websiteFields(document)
  return Object.entries(fields.defaults ?? {})
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([slot, linkIds]) => ({
      slot,
      placements: linkIds.map((linkId) => {
        const child = childByLinkId(document.children, linkId)
        return {
          linkId,
          child,
          pin: child ? pinStateOf(child) : undefined,
          targetId: child && isLinked(child) ? child.id : undefined,
        }
      }),
    }))
}

/** The canonical hostname — the address every other binding redirects to. */
export function canonicalHost(fields: WebsiteFields): string | undefined {
  return fields.domains?.find((domain) => domain.canonical)?.host
}
